import buttonConfiguration from '@/static/pet/button.json';
import doggyConfiguration from '@/static/pet/doggy.json';
import doggyLoopConfiguration from '@/static/pet/doggy_loop.json';
import kittyConfiguration from '@/static/pet/kitty.json';
import kittyLoopConfiguration from '@/static/pet/kitty_loop.json';
import { useLottie } from '@lemonade-hq/cdk';
import lottie, { type AnimationItem } from 'lottie-web';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { PetLandingPageType } from './shared';

interface UsePetBouncingButtonAnimationReturnType {
  petWrapperNodeRef: React.RefObject<HTMLDivElement>;
  buttonWrapperNodeRef: React.RefObject<HTMLDivElement>;
  isPlayed: boolean;
  ref: (node?: Element | null | undefined) => void;
}
export default function usePetBouncingButtonAnimation(
  petLandingPageType: PetLandingPageType,
): UsePetBouncingButtonAnimationReturnType {
  const played = useRef(false);
  const petWrapperNodeRef = useRef<HTMLDivElement | null>(null);
  const buttonWrapperNodeRef = useRef<HTMLDivElement | null>(null);
  const petAnimationItem = useRef<null | AnimationItem>(null);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.8,
  });

  const lottieButtonReference = useLottie(buttonWrapperNodeRef, {
    animationData: buttonConfiguration,
    lottieConfig: {
      loop: false,
    },
  });

  useEffect(() => {
    if (petAnimationItem.current == null || !inView) {
      return;
    }
    if (lottieButtonReference) {
      lottieButtonReference.play();
    }
    if (!played.current) {
      petAnimationItem.current.play();
      played.current = true;
    }
  }, [lottieButtonReference, inView]);

  useEffect(() => {
    if (petAnimationItem.current !== null) return;
    if (petWrapperNodeRef.current == null) return;

    // eslint-disable-next-line no-param-reassign
    petAnimationItem.current = lottie.loadAnimation({
      container: petWrapperNodeRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData: petLandingPageType === PetLandingPageType.Cat ? kittyConfiguration : doggyConfiguration,
    });

    petAnimationItem.current.addEventListener('complete', () => {
      if (petAnimationItem.current == null || petWrapperNodeRef.current == null) return;
      petAnimationItem.current.destroy();

      // eslint-disable-next-line no-param-reassign
      petAnimationItem.current = lottie.loadAnimation({
        container: petWrapperNodeRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: petLandingPageType === PetLandingPageType.Cat ? kittyLoopConfiguration : doggyLoopConfiguration,
      });

      petAnimationItem.current.play();
    });
  }, [petLandingPageType]);

  return {
    petWrapperNodeRef,
    buttonWrapperNodeRef,
    isPlayed: Boolean(played.current),
    ref,
  };
}
